import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
} from '@epicuro-next/platform/storage';
import { loginSuccess } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  debounceTime,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../actions/user-ui.actions';
import { selectStringifiedUserUiState } from '../selectors/user-ui.selectors';

/** local storage toggle button key */
const USER_UI_STORAGE_KEY = 'userUiState';
const SAFE_CONTINIOUS_EVENT_DEBOUNCE = 200;

@Injectable()
export class UserUiEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    @Inject(BROWSER_STORAGE_TOKEN)
    private storageService: BROWSER_STORAGE_SERVICE,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  public onInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      map(() => actions.loadUserUiState()),
    ),
  );

  public setUserUiState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.setUserUiState),
        withLatestFrom(this.store.pipe(select(selectStringifiedUserUiState))),
        tap(([_, state]) =>
          this.storageService.set(USER_UI_STORAGE_KEY, state),
        ),
      ),
    { dispatch: false },
  );

  public loadUserUiState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadUserUiState),
      switchMap(() => of(this.storageService.get(USER_UI_STORAGE_KEY))),
      map((stringifiedState: string | null) => {
        const parsedState =
          stringifiedState !== null ? JSON.parse(stringifiedState) : {};
        const { userMenuOpened, promotedLandingPageFeed, ...state } =
          parsedState;

        return actions.loadUserUiStateSuccess({ state });
      }),
    ),
  );

  public setUserStateToPersistantStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.setAsideMenuOpenedState,
        actions.toggleAsideMenuOpenedState,
        actions.setPBasketView,
        actions.toggleProductDetails,
      ),
      map(() => actions.setUserUiState()),
    ),
  );

  public setMainHeaderHeight$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.setMainHeaderHeight),
        debounceTime(SAFE_CONTINIOUS_EVENT_DEBOUNCE),
        tap((action) => {
          this.document.documentElement.style.setProperty(
            '--main-navigation-height',
            `${action.height}px`,
          );
        }),
      ),
    {
      dispatch: false,
    },
  );
}
